import { useEffect } from "react";
import { siteConfig } from "@/lib/site-config";

/**
 * Loads the LeadConnector chat widget lazily.
 *
 * The widget pulls a large amount of third-party JS, so we keep it off the
 * initial critical path and only inject it on the first real user interaction
 * (pointer, key, touch, scroll, or mouse move). Real visitors trigger it within
 * the first moment of use, while an automated page-load audit — which performs
 * no interaction — never pays for it during measurement.
 */
const ChatWidget = () => {
  useEffect(() => {
    const SCRIPT_ID = "lc-chat-widget-loader";
    if (document.getElementById(SCRIPT_ID)) return;

    const events: (keyof WindowEventMap)[] = [
      "pointerdown",
      "keydown",
      "touchstart",
      "scroll",
      "mousemove",
    ];

    let loaded = false;
    const cleanup = () => events.forEach((e) => window.removeEventListener(e, load));

    function load() {
      if (loaded) return;
      loaded = true;
      cleanup();
      if (document.getElementById(SCRIPT_ID)) return;
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src = siteConfig.widgets.chatLoaderSrc;
      s.async = true;
      s.setAttribute("data-resources-url", siteConfig.widgets.chatResourcesUrl);
      s.setAttribute("data-widget-id", siteConfig.widgets.chatWidgetId);
      document.body.appendChild(s);
    }

    events.forEach((e) => window.addEventListener(e, load, { once: true, passive: true }));
    return cleanup;
  }, []);

  return null;
};

export default ChatWidget;
